import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Loader2, Star, CheckCircle2, ThumbsUp, ThumbsDown, Lock } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { STUDENT_FEEDBACK } from '@/constants/testIds';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';

/* Render one mentor feedback answer based on its field type (rating -> stars). */
const renderMentorAnswer = (field, value) => {
    if (value === undefined || value === null || value === '') {
        return <span className="text-muted-foreground">—</span>;
    }
    if (field?.type === 'rating') {
        return (
            <span className="inline-flex items-center gap-0.5 text-amber-500" aria-label={`${value} out of 5`}>
                {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} className={cn('h-4 w-4', n <= Number(value) ? 'fill-current' : 'opacity-30')} />
                ))}
            </span>
        );
    }
    if (field?.type === 'yesno') return <span className="capitalize">{String(value)}</span>;
    return <span className="whitespace-pre-wrap">{String(value)}</span>;
};

export const StudentFeedbackModal = ({ booking, open, onOpenChange, onSubmitted }) => {
    const [rating, setRating] = useState(0);
    const [hover, setHover] = useState(0);
    const [comments, setComments] = useState('');
    const [recommend, setRecommend] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [existing, setExisting] = useState(null);
    const [canSubmit, setCanSubmit] = useState(true);
    const [loading, setLoading] = useState(false);
    // Mentor's feedback about the candidate (unlocked only after the student submits)
    const [mentorFeedback, setMentorFeedback] = useState(null);
    const [mentorForm, setMentorForm] = useState(null);
    const [mentorAvailable, setMentorAvailable] = useState(false);
    const [mentorLocked, setMentorLocked] = useState(false);

    const loadData = async () => {
        if (!booking) return;
        setLoading(true);
        try {
            const { data } = await api.get(`/student/feedback/${booking.id}`);
            setExisting(data.submission || null);
            setCanSubmit(!!data.can_submit && !data.already_submitted);
            if (data.submission) {
                setRating(data.submission.rating || 0);
                setComments(data.submission.comments || '');
                setRecommend(data.submission.would_recommend ?? null);
            }
            setMentorAvailable(!!data.mentor_feedback_available);
            setMentorLocked(!!data.mentor_feedback_locked);
            setMentorFeedback(data.mentor_feedback || null);
            setMentorForm(data.mentor_feedback_form || null);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not load feedback.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!open || !booking) return;
        setRating(0);
        setHover(0);
        setComments('');
        setRecommend(null);
        setExisting(null);
        setMentorFeedback(null);
        setMentorForm(null);
        setMentorAvailable(false);
        setMentorLocked(false);
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, booking]);

    const submit = async () => {
        if (rating < 1) {
            toast.error('Please select a rating from 1 to 5 stars.');
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post(`/student/feedback/${booking.id}`, {
                rating,
                comments: comments.trim() || undefined,
                would_recommend: recommend,
            });
            toast.success('Thanks for your feedback!');
            setExisting(data.submission);
            setCanSubmit(false);
            onSubmitted?.(data.submission);
            // Refetch so the mentor's feedback (now unlocked) appears
            loadData();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to submit feedback.');
        } finally {
            setSubmitting(false);
        }
    };

    if (!booking) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid={STUDENT_FEEDBACK.modal} className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle className="font-display text-xl">
                        {existing ? 'Your session feedback' : 'Share your feedback'}
                    </DialogTitle>
                    <p className="text-sm text-muted-foreground">
                        Session with <strong>{booking.mentor_name}</strong> · {formatDateTime(booking.start_iso)}
                    </p>
                </DialogHeader>

                {loading ? (
                    <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                    </div>
                ) : existing ? (
                    <div className="space-y-4 py-2">
                        <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
                            <CheckCircle2 className="h-4 w-4" />
                            Feedback submitted on {formatDateTime(existing.submitted_at)}
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Your rating</Label>
                            <div className="mt-1 flex items-center gap-1">
                                {[1, 2, 3, 4, 5].map((n) => (
                                    <Star
                                        key={n}
                                        className={cn('h-6 w-6', n <= existing.rating ? 'fill-amber-500 text-amber-500' : 'text-muted-foreground')}
                                    />
                                ))}
                                <span className="ml-2 text-sm tabular-nums text-muted-foreground">{existing.rating}/5</span>
                            </div>
                        </div>
                        {existing.comments && (
                            <div>
                                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Comments</Label>
                                <p className="mt-1 whitespace-pre-line rounded-lg border border-border/60 bg-card p-3 text-sm">
                                    {existing.comments}
                                </p>
                            </div>
                        )}
                        {existing.would_recommend !== null && existing.would_recommend !== undefined && (
                            <div>
                                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Would you recommend this mentor?</Label>
                                <div className="mt-1">
                                    <Badge variant={existing.would_recommend ? 'default' : 'outline'}>
                                        {existing.would_recommend ? 'Yes' : 'No'}
                                    </Badge>
                                </div>
                            </div>
                        )}
                    </div>
                ) : !canSubmit ? (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                        Feedback opens after the session ends. Please come back after your scheduled time to share your thoughts.
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div>
                            <Label className="text-sm">How was your session? <span className="text-destructive">*</span></Label>
                            <div className="mt-2 flex items-center gap-1">
                                {[1, 2, 3, 4, 5].map((n) => (
                                    <button
                                        key={n}
                                        type="button"
                                        data-testid={`${STUDENT_FEEDBACK.star}-${n}`}
                                        onMouseEnter={() => setHover(n)}
                                        onMouseLeave={() => setHover(0)}
                                        onClick={() => setRating(n)}
                                        className="rounded p-1 outline-none transition-transform hover:scale-110 focus-visible:ring-2 focus-visible:ring-accent"
                                        aria-label={`Rate ${n} star${n > 1 ? 's' : ''}`}
                                    >
                                        <Star
                                            className={cn(
                                                'h-8 w-8 transition-colors',
                                                n <= (hover || rating) ? 'fill-amber-500 text-amber-500' : 'text-muted-foreground/50',
                                            )}
                                        />
                                    </button>
                                ))}
                                {rating > 0 && (
                                    <span className="ml-2 text-sm font-medium tabular-nums">{rating}/5</span>
                                )}
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="comments" className="text-sm">Comments <span className="text-muted-foreground">(optional)</span></Label>
                            <Textarea
                                id="comments"
                                rows={4}
                                placeholder="What did you like? Anything the mentor could improve?"
                                value={comments}
                                onChange={(e) => setComments(e.target.value)}
                                data-testid={STUDENT_FEEDBACK.comments}
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <Label className="text-sm">Would you recommend this mentor?</Label>
                            <div className="mt-2 flex gap-2">
                                <Button
                                    type="button"
                                    variant={recommend === true ? 'default' : 'outline'}
                                    onClick={() => setRecommend(true)}
                                    data-testid={STUDENT_FEEDBACK.recommend_yes}
                                    size="sm"
                                >
                                    <ThumbsUp className="mr-2 h-4 w-4" /> Yes
                                </Button>
                                <Button
                                    type="button"
                                    variant={recommend === false ? 'default' : 'outline'}
                                    onClick={() => setRecommend(false)}
                                    data-testid={STUDENT_FEEDBACK.recommend_no}
                                    size="sm"
                                >
                                    <ThumbsDown className="mr-2 h-4 w-4" /> No
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Mentor's feedback about the candidate (unlocked after the student submits) */}
                {!loading && (existing || mentorLocked) && (
                    <div className="mt-2 space-y-3 border-t border-border/60 pt-4" data-testid="mentor-feedback-section">
                        <Label className="text-sm font-semibold text-foreground">Your mentor's feedback for you</Label>
                        {mentorLocked ? (
                            <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="mentor-feedback-locked">
                                <Lock className="h-4 w-4 shrink-0" />
                                Submit your rating above to unlock your mentor's feedback for you.
                            </div>
                        ) : mentorFeedback ? (
                            <div className="space-y-3 rounded-lg border border-border/60 bg-card p-3" data-testid="mentor-feedback-content">
                                {(mentorForm?.fields || []).map((f) => (
                                    <div key={f.id}>
                                        <div className="text-xs uppercase tracking-wide text-muted-foreground">{f.label}</div>
                                        <div className="mt-0.5 text-sm">{renderMentorAnswer(f, mentorFeedback.answers?.[f.id])}</div>
                                    </div>
                                ))}
                                <div className="text-[11px] text-muted-foreground">
                                    Shared on {formatDateTime(mentorFeedback.submitted_at)}
                                </div>
                            </div>
                        ) : (
                            <div className="rounded-lg border border-dashed border-border/60 bg-card p-3 text-sm text-muted-foreground" data-testid="mentor-feedback-empty">
                                Your mentor hasn't shared their feedback yet. Check back later.
                            </div>
                        )}
                    </div>
                )}

                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
                    {canSubmit && !existing && (
                        <Button
                            onClick={submit}
                            disabled={submitting || rating < 1}
                            data-testid={STUDENT_FEEDBACK.submit}
                        >
                            {submitting ? (
                                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting…</>
                            ) : (
                                'Submit feedback'
                            )}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
